"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { RefObject, useEffect, useRef, useState } from "react";

interface SearchResultsDemoProps {
  searchRef: RefObject<HTMLDivElement>;
  onDownloadClick?: () => void;
  onShowDownloadChange?: (show: boolean) => void;
}

export function SearchResultsDemo({ searchRef, onDownloadClick, onShowDownloadChange }: SearchResultsDemoProps) {
  const { scrollYProgress } = useScroll({
    target: searchRef,
    offset: ["start center", "end end"],
  });

  // 视频播放状态
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // 基于滚动进度控制视频显示 - 第二阶段 (0.7-1.0, 占总进度30%)
  const showVideo = useTransform(scrollYProgress, [0.7, 0.75, 0.9, 1.0], [0, 1, 1, 1]);


  // 视频播放控制 - 基于 showVideo motion value
  useEffect(() => {
    const unsubscribe = showVideo.on("change", (value) => {
      const shouldPlay = value > 0;
      
      if (videoRef.current) {
        if (shouldPlay && !videoPlaying) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
          setVideoPlaying(true);
        } else if (!shouldPlay && videoPlaying) {
          videoRef.current.pause();
          setVideoPlaying(false);
        }
      }
    });

    return unsubscribe;
  }, [showVideo, videoPlaying]);

  // 文本覆盖层控制已转移到父组件


  const windows = [
    {
      name: "Google",
      color: "bg-white/10 border-white/10",
      id: 1,
      searchElements: {
        searchBar: true,
        resultCount: "2,340,000 results",
        hasSnippets: true,
        hasImages: false,
      },
    },
    {
      name: "Reddit",
      color: "bg-white/10 border-white/10",
      id: 2,
      searchElements: {
        searchBar: false,
        resultCount: "156 comments",
        hasSnippets: true,
        hasImages: false,
      },
    },
    {
      name: "X (Twitter)",
      color: "bg-white/10 border-white/10",
      id: 3,
      searchElements: {
        searchBar: true,
        resultCount: "12.3K posts",
        hasSnippets: false,
        hasImages: true,
      },
    },
    {
      name: "RedNote",
      color: "bg-white/10 border-white/10",
      id: 4,
      searchElements: {
        searchBar: true,
        resultCount: "1,247 notes",
        hasSnippets: true,
        hasImages: true,
      },
    },
    {
      name: "YouTube",
      color: "bg-white/10 border-white/10",
      id: 5,
      searchElements: {
        searchBar: true,
        resultCount: "500K subs",
        hasSnippets: false,
        hasImages: true,
      },
    },
    {
      name: "TikTok",
      color: "bg-white/10 border-white/10",
      id: 6,
      searchElements: {
        searchBar: false,
        resultCount: "120M views",
        hasSnippets: false,
        hasImages: true,
      },
    },
    {
      name: "LinkedIn",
      color: "bg-white/10 border-white/10",
      id: 7,
      searchElements: {
        searchBar: true,
        resultCount: "2,156 profiles",
        hasSnippets: true,
        hasImages: false,
      },
    },
    {
      name: "GitHub",
      color: "bg-white/10 border-white/10",
      id: 8,
      searchElements: {
        searchBar: true,
        resultCount: "15.2k stars",
        hasSnippets: true,
        hasImages: false,
      },
    },
  ];

  // 浏览器整体出现动画 - 扩展时间
  const browserOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

  // 8个窗口的出现动画 - 从左侧(-200px)开始，扩展时间 (0.1-0.42)
  const window1AppearX = useTransform(scrollYProgress, [0.1, 0.14], [-200, 0]);
  const window2AppearX = useTransform(scrollYProgress, [0.14, 0.18], [-200, 0]);
  const window3AppearX = useTransform(scrollYProgress, [0.18, 0.22], [-200, 0]);
  const window4AppearX = useTransform(scrollYProgress, [0.22, 0.26], [-200, 0]);
  const window5AppearX = useTransform(scrollYProgress, [0.26, 0.3], [-200, 0]);
  const window6AppearX = useTransform(scrollYProgress, [0.3, 0.34], [-200, 0]);
  const window7AppearX = useTransform(scrollYProgress, [0.34, 0.38], [-200, 0]);
  const window8AppearX = useTransform(scrollYProgress, [0.38, 0.42], [-200, 0]);

  // 对应的透明度动画
  const window1AppearOpacity = useTransform(scrollYProgress, [0.1, 0.14], [0, 1]);
  const window2AppearOpacity = useTransform(scrollYProgress, [0.14, 0.18], [0, 1]);
  const window3AppearOpacity = useTransform(scrollYProgress, [0.18, 0.22], [0, 1]);
  const window4AppearOpacity = useTransform(scrollYProgress, [0.22, 0.26], [0, 1]);
  const window5AppearOpacity = useTransform(scrollYProgress, [0.26, 0.3], [0, 1]);
  const window6AppearOpacity = useTransform(scrollYProgress, [0.3, 0.34], [0, 1]);
  const window7AppearOpacity = useTransform(scrollYProgress, [0.34, 0.38], [0, 1]);
  const window8AppearOpacity = useTransform(scrollYProgress, [0.38, 0.42], [0, 1]);

  const windowAppearXTransforms = [
    window1AppearX,
    window2AppearX,
    window3AppearX,
    window4AppearX,
    window5AppearX,
    window6AppearX,
    window7AppearX,
    window8AppearX,
  ];
  const windowAppearOpacityTransforms = [
    window1AppearOpacity,
    window2AppearOpacity,
    window3AppearOpacity,
    window4AppearOpacity,
    window5AppearOpacity,
    window6AppearOpacity,
    window7AppearOpacity,
    window8AppearOpacity,
  ];

  // 窗口退场动画 - 从右侧移动出去 (0.5-0.68)
  const window8ExitX = useTransform(scrollYProgress, [0.5, 0.52], [0, 200]);
  const window7ExitX = useTransform(scrollYProgress, [0.52, 0.54], [0, 200]);
  const window6ExitX = useTransform(scrollYProgress, [0.54, 0.56], [0, 200]);
  const window5ExitX = useTransform(scrollYProgress, [0.56, 0.58], [0, 200]);
  const window4ExitX = useTransform(scrollYProgress, [0.58, 0.6], [0, 200]);
  const window3ExitX = useTransform(scrollYProgress, [0.6, 0.62], [0, 200]);
  const window2ExitX = useTransform(scrollYProgress, [0.62, 0.64], [0, 200]);
  const window1ExitX = useTransform(scrollYProgress, [0.64, 0.66], [0, 200]);

  // 透明度动画 - 配合右移效果
  const window8ExitOpacity = useTransform(scrollYProgress, [0.5, 0.52], [1, 0]);
  const window7ExitOpacity = useTransform(scrollYProgress, [0.52, 0.54], [1, 0]);
  const window6ExitOpacity = useTransform(scrollYProgress, [0.54, 0.56], [1, 0]);
  const window5ExitOpacity = useTransform(scrollYProgress, [0.56, 0.58], [1, 0]);
  const window4ExitOpacity = useTransform(scrollYProgress, [0.58, 0.6], [1, 0]);
  const window3ExitOpacity = useTransform(scrollYProgress, [0.6, 0.62], [1, 0]);
  const window2ExitOpacity = useTransform(scrollYProgress, [0.62, 0.64], [1, 0]);
  const window1ExitOpacity = useTransform(scrollYProgress, [0.64, 0.66], [1, 0]);

  const windowExitXTransforms = [
    window1ExitX,
    window2ExitX,
    window3ExitX,
    window4ExitX,
    window5ExitX,
    window6ExitX,
    window7ExitX,
    window8ExitX,
  ];
  const windowExitOpacityTransforms = [
    window1ExitOpacity,
    window2ExitOpacity,
    window3ExitOpacity,
    window4ExitOpacity,
    window5ExitOpacity,
    window6ExitOpacity,
    window7ExitOpacity,
    window8ExitOpacity,
  ];

  // 组合X轴变换 - 将入场和退场的X轴动画合并
  const combinedX1 = useTransform([window1AppearX, window1ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX2 = useTransform([window2AppearX, window2ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX3 = useTransform([window3AppearX, window3ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX4 = useTransform([window4AppearX, window4ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX5 = useTransform([window5AppearX, window5ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX6 = useTransform([window6AppearX, window6ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX7 = useTransform([window7AppearX, window7ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX8 = useTransform([window8AppearX, window8ExitX], ([appear, exit]) => (appear as number) + (exit as number));

  const combinedXTransforms = [combinedX1, combinedX2, combinedX3, combinedX4, combinedX5, combinedX6, combinedX7, combinedX8];

  // 组合透明度变换
  const combinedOpacity1 = useTransform([window1AppearOpacity, window1ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity2 = useTransform([window2AppearOpacity, window2ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity3 = useTransform([window3AppearOpacity, window3ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity4 = useTransform([window4AppearOpacity, window4ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity5 = useTransform([window5AppearOpacity, window5ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity6 = useTransform([window6AppearOpacity, window6ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity7 = useTransform([window7AppearOpacity, window7ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity8 = useTransform([window8AppearOpacity, window8ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );

  const combinedOpacityTransforms = [
    combinedOpacity1,
    combinedOpacity2,
    combinedOpacity3,
    combinedOpacity4,
    combinedOpacity5,
    combinedOpacity6,
    combinedOpacity7,
    combinedOpacity8,
  ];

  return (
    <div className="w-full relative flex items-center justify-center">
        {/* 视频容器 - 基于滚动进度显示 (第二阶段) */}
        <motion.div
          className="absolute inset-0 z-20 flex items-center justify-center"
          style={{ 
            opacity: showVideo,
            x: useTransform(showVideo, [0, 1], [-100, 0]),
            filter: useTransform(showVideo, [0, 1], ["blur(4px)", "blur(0px)"])
          }}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-2xl"
            src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Deep+search.webm"
            autoPlay
            loop
            muted
            playsInline
          />
        </motion.div>

        {/* Wireframe容器 - 在第二阶段时逐渐隐藏 */}
        <motion.div
          className="w-[700px] h-[500px] border-2 border-white/10 rounded-lg bg-white/10 relative overflow-hidden"
          style={{ 
            opacity: useTransform(showVideo, [0, 0.1, 0.5, 1], [1, 1, 0.3, 0])
          }}
        >
          {/* Browser Header */}
          <div className="flex items-center gap-2 p-4 border-b border-white/10">
            {/* Traffic Lights */}
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
              <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
              <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
            </div>

            {/* Address Bar */}
            <div className="flex-1 ml-4">
              <div className="h-8 bg-white/5 border border-white/10 rounded px-3 flex items-center">
                <div className="w-4 h-4 border border-white/20 rounded-sm mr-2"></div>
                <div className="h-2 bg-white/20 rounded flex-1"></div>
              </div>
            </div>

            {/* Nav Buttons */}
            <div className="flex gap-2 ml-4">
              <div className="w-8 h-8 border border-white/20 rounded"></div>
              <div className="w-8 h-8 border border-white/20 rounded"></div>
            </div>
          </div>

          {/* Browser Content - 8个方形窗口平铺 (2行4列) */}
          <div className="p-3 relative" style={{ height: "calc(100% - 60px)" }}>
            <div className="grid grid-cols-4 grid-rows-2 gap-2 h-full">
              {windows.map((window, index) => (
                <motion.div
                  key={window.id}
                  className={`aspect-square border rounded relative overflow-hidden ${window.color}`}
                  style={{
                    x: combinedXTransforms[index],
                    opacity: combinedOpacityTransforms[index],
                  }}
                >
                  {/* 窗口内容 - 模拟搜索界面 */}
                  <div className="p-1.5 h-full flex flex-col text-[6px]">
                    {/* 搜索栏 (如果有) */}
                    {window.searchElements.searchBar && (
                      <div className="h-2 bg-white/5 border border-white/10 rounded mb-1.5 flex items-center px-1">
                        <div className="w-1 h-1 bg-white/20 rounded-full mr-1"></div>
                        <div className="h-0.5 bg-white/15 rounded flex-1"></div>
                      </div>
                    )}

                    {/* 结果计数 */}
                    <div className="h-1 bg-white/10 rounded w-3/4 mb-1.5"></div>

                    {/* 搜索结果内容 */}
                    <div className="flex-1 space-y-1">
                      {window.searchElements.hasImages ? (
                        <>
                          {/* 有图片的布局 (社交媒体风格) */}
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-white/10 rounded flex-shrink-0"></div>
                            <div className="flex-1 space-y-0.5">
                              <div className="h-0.5 bg-white/15 rounded w-full"></div>
                              <div className="h-0.5 bg-white/10 rounded w-2/3"></div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-white/10 rounded flex-shrink-0"></div>
                            <div className="flex-1 space-y-0.5">
                              <div className="h-0.5 bg-white/15 rounded w-5/6"></div>
                              <div className="h-0.5 bg-white/10 rounded w-1/2"></div>
                            </div>
                          </div>
                        </>
                      ) : window.searchElements.hasSnippets ? (
                        <>
                          {/* 有摘要的布局 (搜索结果风格) */}
                          <div className="space-y-0.5">
                            <div className="h-0.5 bg-white/20 rounded w-4/5"></div>
                            <div className="h-0.5 bg-white/10 rounded w-full"></div>
                            <div className="h-0.5 bg-white/10 rounded w-3/4"></div>
                            <div className="h-0.5 bg-white/10 rounded w-2/3"></div>
                          </div>
                          <div className="space-y-0.5 mt-1">
                            <div className="h-0.5 bg-white/20 rounded w-3/4"></div>
                            <div className="h-0.5 bg-white/10 rounded w-5/6"></div>
                            <div className="h-0.5 bg-white/10 rounded w-1/2"></div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* 简单列表布局 */}
                          <div className="h-0.5 bg-white/15 rounded w-full"></div>
                          <div className="h-0.5 bg-white/15 rounded w-4/5"></div>
                          <div className="h-0.5 bg-white/15 rounded w-3/4"></div>
                          <div className="h-0.5 bg-white/15 rounded w-5/6"></div>
                          <div className="h-0.5 bg-white/15 rounded w-2/3"></div>
                        </>
                      )}
                    </div>

                    {/* 底部状态/统计 */}
                    <div className="mt-1 flex justify-between items-center">
                      <div className="h-0.5 bg-white/20 rounded w-1/3"></div>
                      <div className="w-1 h-1 bg-white/15 rounded"></div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Loading Indicator - 与8个窗口出现动画同步 */}
          <motion.div
            className="absolute top-0 left-0 right-0 h-1 bg-white/10 rounded-t-lg"
            style={{
              scaleX: useTransform(scrollYProgress, [0.1, 0.42], [0, 1]),
              transformOrigin: "left",
            }}
          />
        </motion.div>
      </div>
  );
}
